// lib/color-harmonization.ts
import { createColorHarmonizationDiagnostic } from '#lib/color-harmonization-diagnostics';

/**
 * Applies a low-frequency Lab correction to a high-resolution target without
 * allocating full-resolution OpenCV intermediates.
 */
type ColorHarmonizationDimensions = { width: number; height: number };
type ColorHarmonizationRegion = { x: number; y: number; width: number; height: number };
type ColorHarmonizationMatDiagnostic = {
  name: string;
  channels: number | null;
  depth: number | null;
  rows: number | null;
  cols: number | null;
};
type HarmonizationMatReference = { name: string; get: () => any };

const readHarmonizationMatValue = (mat: any, key: 'channels' | 'depth' | 'rows' | 'cols'): number | null => {
  try {
    const value = typeof mat?.[key] === 'function' ? mat[key]() : mat?.[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

const describeHarmonizationMat = ({ name, get }: HarmonizationMatReference): ColorHarmonizationMatDiagnostic | null => {
  let mat: any;
  try {
    mat = get();
  } catch {
    return null;
  }
  if (!mat) return null;
  return {
    name,
    channels: readHarmonizationMatValue(mat, 'channels'),
    depth: readHarmonizationMatValue(mat, 'depth'),
    rows: readHarmonizationMatValue(mat, 'rows'),
    cols: readHarmonizationMatValue(mat, 'cols'),
  };
};

const decodeHarmonizationError = (error: unknown, cv: any): unknown => {
  if (typeof error !== 'number' || typeof cv?.exceptionFromPtr !== 'function') return error;
  try {
    return cv.exceptionFromPtr(error) ?? error;
  } catch {
    return error;
  }
};

export async function performColorHarmonization(
  sourceImageData: ImageData,
  targetImageData: ImageData,
  cv: any,
  harmonizeProfile: 'soft' | 'strict' = 'soft',
  outputTargetImageData: ImageData = targetImageData,
): Promise<ImageData | null> {
  let sourceMat: any, targetMat: any, resizedSource: any, sourceRgb: any, targetRgb: any, sourceLab: any, targetLab: any,
      sourceLab_32f: any, targetLab_32f: any, diffLab_32f: any, blurredDiffLab_32f: any,
      targetRgbaChannels: any, originalAlpha: any, opaqueMask: any, transparentMask: any, precompositedTargetMat: any;
  let stage = 'initialization';
  let activeTile: ColorHarmonizationRegion | undefined;
  let activeRoi: ColorHarmonizationRegion | undefined;
  let activeTileMats: HarmonizationMatReference[] = [];
  let failureMatDiagnostics: ColorHarmonizationMatDiagnostic[] | undefined;
  const dimensions: {
    source: ColorHarmonizationDimensions;
    analysis: ColorHarmonizationDimensions;
    target: ColorHarmonizationDimensions;
    output: ColorHarmonizationDimensions;
  } = {
    source: { width: 0, height: 0 },
    analysis: { width: 0, height: 0 },
    target: { width: 0, height: 0 },
    output: { width: 0, height: 0 },
  };
  const matReferences: HarmonizationMatReference[] = [
    { name: 'sourceMat', get: () => sourceMat },
    { name: 'targetMat', get: () => targetMat },
    { name: 'resizedSource', get: () => resizedSource },
    { name: 'sourceRgb', get: () => sourceRgb },
    { name: 'targetRgb', get: () => targetRgb },
    { name: 'sourceLab', get: () => sourceLab },
    { name: 'targetLab', get: () => targetLab },
    { name: 'sourceLab_32f', get: () => sourceLab_32f },
    { name: 'targetLab_32f', get: () => targetLab_32f },
    { name: 'diffLab_32f', get: () => diffLab_32f },
    { name: 'blurredDiffLab_32f', get: () => blurredDiffLab_32f },
    { name: 'targetRgbaChannels', get: () => targetRgbaChannels },
    { name: 'originalAlpha', get: () => originalAlpha },
    { name: 'opaqueMask', get: () => opaqueMask },
    { name: 'transparentMask', get: () => transparentMask },
    { name: 'precompositedTargetMat', get: () => precompositedTargetMat },
  ];
  const collectMatDiagnostics = (): ColorHarmonizationMatDiagnostic[] => (
    [...matReferences, ...activeTileMats]
      .map(describeHarmonizationMat)
      .filter((mat): mat is ColorHarmonizationMatDiagnostic => mat !== null)
  );

  try {
    stage = 'input-dimension-validation';
    dimensions.source = { width: sourceImageData.width, height: sourceImageData.height };
    dimensions.target = { width: targetImageData.width, height: targetImageData.height };
    dimensions.output = { width: outputTargetImageData.width, height: outputTargetImageData.height };
    stage = 'source-mat-from-image-data';
    sourceMat = cv.matFromImageData(sourceImageData);
    stage = 'target-mat-from-image-data';
    targetMat = cv.matFromImageData(targetImageData);
    stage = 'source-target-dimension-validation';
    dimensions.analysis = { width: targetMat.cols, height: targetMat.rows };

    if (sourceMat.cols !== targetMat.cols || sourceMat.rows !== targetMat.rows) {
      stage = 'source-resize-allocation';
      resizedSource = new cv.Mat();
      stage = 'source-resize';
      cv.resize(sourceMat, resizedSource, new cv.Size(targetMat.cols, targetMat.rows), 0, 0, cv.INTER_AREA);
      sourceMat.delete();
      sourceMat = resizedSource;
      resizedSource = null;
    }

    stage = 'alpha-split-allocation';
    targetRgbaChannels = new cv.MatVector();
    stage = 'alpha-split';
    cv.split(targetMat, targetRgbaChannels);
    stage = 'alpha-channel-read';
    originalAlpha = targetRgbaChannels.get(3);
    stage = 'alpha-threshold-allocation';
    opaqueMask = new cv.Mat();
    stage = 'alpha-threshold';
    cv.threshold(originalAlpha, opaqueMask, 10, 255, cv.THRESH_BINARY);
    stage = 'alpha-mask-invert-allocation';
    transparentMask = new cv.Mat();
    stage = 'alpha-mask-invert';
    cv.bitwise_not(opaqueMask, transparentMask);
    originalAlpha.delete();
    originalAlpha = null;

    stage = 'precomposition';
    precompositedTargetMat = targetMat.clone();
    stage = 'precomposition-copy';
    sourceMat.copyTo(precompositedTargetMat, transparentMask);
    stage = 'source-rgb-allocation';
    sourceRgb = new cv.Mat();
    stage = 'target-rgb-allocation';
    targetRgb = new cv.Mat();
    stage = 'source-rgb-conversion';
    cv.cvtColor(sourceMat, sourceRgb, cv.COLOR_RGBA2RGB);
    stage = 'target-rgb-conversion';
    cv.cvtColor(precompositedTargetMat, targetRgb, cv.COLOR_RGBA2RGB);
    stage = 'source-lab-allocation';
    sourceLab = new cv.Mat();
    stage = 'target-lab-allocation';
    targetLab = new cv.Mat();
    stage = 'source-lab-conversion';
    cv.cvtColor(sourceRgb, sourceLab, cv.COLOR_RGB2Lab);
    stage = 'target-lab-conversion';
    cv.cvtColor(targetRgb, targetLab, cv.COLOR_RGB2Lab);
    stage = 'source-lab-float-allocation';
    sourceLab_32f = new cv.Mat();
    stage = 'target-lab-float-allocation';
    targetLab_32f = new cv.Mat();
    stage = 'source-lab-float-conversion';
    sourceLab.convertTo(sourceLab_32f, cv.CV_32F);
    stage = 'target-lab-float-conversion';
    targetLab.convertTo(targetLab_32f, cv.CV_32F);
    stage = 'lab-subtraction-allocation';
    diffLab_32f = new cv.Mat();
    stage = 'lab-subtraction';
    cv.subtract(sourceLab_32f, targetLab_32f, diffLab_32f);
    const analysisWidth = targetMat.cols;
    const analysisHeight = targetMat.rows;
    sourceMat.delete(); sourceMat = null;
    targetMat.delete(); targetMat = null;
    resizedSource?.delete(); resizedSource = null;
    sourceRgb.delete(); sourceRgb = null;
    targetRgb.delete(); targetRgb = null;
    sourceLab.delete(); sourceLab = null;
    targetLab.delete(); targetLab = null;
    targetLab_32f.delete(); targetLab_32f = null;
    targetRgbaChannels.delete(); targetRgbaChannels = null;
    opaqueMask.delete(); opaqueMask = null;
    transparentMask.delete(); transparentMask = null;
    precompositedTargetMat.delete(); precompositedTargetMat = null;
    if (harmonizeProfile === 'soft') {
      sourceLab_32f.delete(); sourceLab_32f = null;
    }
    stage = 'gaussian-blur-allocation';
    blurredDiffLab_32f = new cv.Mat();
    stage = 'gaussian-blur';
    cv.GaussianBlur(diffLab_32f, blurredDiffLab_32f, new cv.Size(81, 81), 0, 0, cv.BORDER_REPLICATE);

    stage = 'analysis-output-dimension-validation';
    const outputWidth = outputTargetImageData.width;
    const outputHeight = outputTargetImageData.height;
    dimensions.output = { width: outputWidth, height: outputHeight };
    if (outputWidth <= 0 || outputHeight <= 0) throw new Error('Cannot harmonize invalid target dimensions.');

    stage = 'output-buffer-allocation';
    const outputData = new Uint8ClampedArray(outputTargetImageData.data);
    const tileSize = 1024;
    stage = 'analysis-dimension-validation';
    dimensions.analysis = { width: analysisWidth, height: analysisHeight };

    for (let y = 0; y < outputHeight; y += tileSize) {
      for (let x = 0; x < outputWidth; x += tileSize) {
        const tileWidth = Math.min(tileSize, outputWidth - x);
        const tileHeight = Math.min(tileSize, outputHeight - y);
        activeTile = { x, y, width: tileWidth, height: tileHeight };
        activeRoi = undefined;
        activeTileMats = [];
        const analysisX = Math.floor(x * analysisWidth / outputWidth);
        const analysisY = Math.floor(y * analysisHeight / outputHeight);
        const analysisXEnd = Math.min(analysisWidth, Math.max(analysisX + 1, Math.ceil((x + tileWidth) * analysisWidth / outputWidth)));
        const analysisYEnd = Math.min(analysisHeight, Math.max(analysisY + 1, Math.ceil((y + tileHeight) * analysisHeight / outputHeight)));
        const roi = { x: analysisX, y: analysisY, width: analysisXEnd - analysisX, height: analysisYEnd - analysisY };
        activeRoi = roi;
        stage = 'roi-creation';
        const analysisRect = new cv.Rect(roi.x, roi.y, roi.width, roi.height);
        stage = 'tile-extraction-allocation';
        const tileData = new Uint8ClampedArray(tileWidth * tileHeight * 4);

        stage = 'tile-extraction';
        for (let tileY = 0; tileY < tileHeight; tileY += 1) {
          const sourceOffset = ((y + tileY) * outputWidth + x) * 4;
          tileData.set(outputTargetImageData.data.subarray(sourceOffset, sourceOffset + tileWidth * 4), tileY * tileWidth * 4);
        }

        let tileMat: any, tileRgb: any, tileLab: any, tileLab32f: any, correctionRoi: any, correction: any,
            correctedLab32f: any, correctedLab: any, correctedRgb: any, correctedChannels: any, sourceChannels: any,
            sourceChromaRoi: any, sourceChroma: any, strictLab: any, sourceA: any, sourceB: any, correctedA: any, correctedB: any;
        activeTileMats = [
          { name: 'tileMat', get: () => tileMat },
          { name: 'tileRgb', get: () => tileRgb },
          { name: 'tileLab', get: () => tileLab },
          { name: 'tileLab32f', get: () => tileLab32f },
          { name: 'correctionRoi', get: () => correctionRoi },
          { name: 'correction', get: () => correction },
          { name: 'correctedLab32f', get: () => correctedLab32f },
          { name: 'correctedLab', get: () => correctedLab },
          { name: 'correctedRgb', get: () => correctedRgb },
          { name: 'correctedChannels', get: () => correctedChannels },
          { name: 'sourceChannels', get: () => sourceChannels },
          { name: 'sourceChromaRoi', get: () => sourceChromaRoi },
          { name: 'sourceChroma', get: () => sourceChroma },
          { name: 'strictLab', get: () => strictLab },
        ];
        try {
          stage = 'tile-image-data';
          const tileImageData = new ImageData(tileData, tileWidth, tileHeight);
          stage = 'tile-mat-from-image-data';
          tileMat = cv.matFromImageData(tileImageData);
          stage = 'tile-rgb-allocation';
          tileRgb = new cv.Mat();
          stage = 'tile-lab-allocation';
          tileLab = new cv.Mat();
          stage = 'tile-lab-float-allocation';
          tileLab32f = new cv.Mat();
          stage = 'tile-rgb-conversion';
          cv.cvtColor(tileMat, tileRgb, cv.COLOR_RGBA2RGB);
          stage = 'tile-lab-conversion';
          cv.cvtColor(tileRgb, tileLab, cv.COLOR_RGB2Lab);
          stage = 'tile-lab-float-conversion';
          tileLab.convertTo(tileLab32f, cv.CV_32F);

          activeRoi = roi;
          stage = 'correction-roi-creation';
          correctionRoi = blurredDiffLab_32f.roi(analysisRect);
          stage = 'correction-resize-allocation';
          correction = new cv.Mat();
          stage = 'correction-resize';
          cv.resize(correctionRoi, correction, new cv.Size(tileWidth, tileHeight), 0, 0, cv.INTER_LINEAR);
          stage = 'correction-application-allocation';
          correctedLab32f = new cv.Mat();
          stage = 'correction-application';
          cv.add(tileLab32f, correction, correctedLab32f);

          if (harmonizeProfile === 'strict') {
            stage = 'strict-channel-merge-allocation';
            correctedChannels = new cv.MatVector();
            sourceChannels = new cv.MatVector();
            activeRoi = roi;
            stage = 'strict-source-chroma-roi-creation';
            sourceChromaRoi = sourceLab_32f.roi(analysisRect);
            stage = 'strict-source-chroma-allocation';
            sourceChroma = new cv.Mat();
            stage = 'strict-source-chroma-resize';
            cv.resize(sourceChromaRoi, sourceChroma, new cv.Size(tileWidth, tileHeight), 0, 0, cv.INTER_LINEAR);
            stage = 'strict-channel-split';
            cv.split(correctedLab32f, correctedChannels);
            stage = 'strict-source-channel-split';
            cv.split(sourceChroma, sourceChannels);
            stage = 'strict-channel-merge';
            // MatVector.get() returns a new Mat holding a reference to the channel: undeleted, it keeps the tile's
            // channel buffer alive in the WASM heap after the vector itself is deleted.
            sourceA = sourceChannels.get(1);
            sourceB = sourceChannels.get(2);
            correctedA = correctedChannels.get(1);
            correctedB = correctedChannels.get(2);
            sourceA.copyTo(correctedA);
            sourceB.copyTo(correctedB);
            stage = 'strict-lab-allocation';
            strictLab = new cv.Mat();
            stage = 'strict-channel-merge';
            cv.merge(correctedChannels, strictLab);
            correctedLab32f.delete();
            correctedLab32f = strictLab;
            strictLab = null;
          }

          stage = 'output-lab-allocation';
          correctedLab = new cv.Mat();
          stage = 'output-rgb-allocation';
          correctedRgb = new cv.Mat();
          stage = 'output-lab-conversion';
          correctedLab32f.convertTo(correctedLab, cv.CV_8U);
          stage = 'output-rgb-conversion';
          cv.cvtColor(correctedLab, correctedRgb, cv.COLOR_Lab2RGB);
          stage = 'output-write';
          for (let tileY = 0; tileY < tileHeight; tileY += 1) {
            for (let tileX = 0; tileX < tileWidth; tileX += 1) {
              const rgbOffset = (tileY * tileWidth + tileX) * 3;
              const outputOffset = ((y + tileY) * outputWidth + x + tileX) * 4;
              outputData[outputOffset] = correctedRgb.data[rgbOffset];
              outputData[outputOffset + 1] = correctedRgb.data[rgbOffset + 1];
              outputData[outputOffset + 2] = correctedRgb.data[rgbOffset + 2];
            }
          }
        } catch (error) {
          failureMatDiagnostics = collectMatDiagnostics();
          throw error;
        } finally {
          tileMat?.delete(); tileRgb?.delete(); tileLab?.delete(); tileLab32f?.delete();
          correctionRoi?.delete(); correction?.delete(); correctedLab32f?.delete();
          correctedLab?.delete(); correctedRgb?.delete(); correctedChannels?.delete();
          sourceChannels?.delete(); sourceChromaRoi?.delete(); sourceChroma?.delete(); strictLab?.delete();
          sourceA?.delete(); sourceB?.delete(); correctedA?.delete(); correctedB?.delete();
        }
      }
    }

    activeTile = undefined;
    activeRoi = undefined;
    activeTileMats = [];
    stage = 'output-image-data';
    return new ImageData(outputData, outputWidth, outputHeight);
  } catch (error) {
    const diagnostic = createColorHarmonizationDiagnostic({
      stage,
      harmonizeProfile,
      dimensions,
      tile: activeTile,
      roi: activeRoi,
      mats: failureMatDiagnostics ?? collectMatDiagnostics(),
      error: decodeHarmonizationError(error, cv),
    });
    console.error('[Harmonize] Color harmonization failed', diagnostic);
    return null;
  } finally {
    sourceMat?.delete(); targetMat?.delete(); resizedSource?.delete(); sourceRgb?.delete(); targetRgb?.delete();
    sourceLab?.delete(); targetLab?.delete(); sourceLab_32f?.delete(); targetLab_32f?.delete();
    diffLab_32f?.delete(); blurredDiffLab_32f?.delete(); originalAlpha?.delete(); targetRgbaChannels?.delete();
    opaqueMask?.delete(); transparentMask?.delete(); precompositedTargetMat?.delete();
  }
}
